// React
import { PropsWithChildren } from "react"

// Third-Party Libraries
import { type LinkProps, Link } from "react-router-dom"

// Utils
import { usePermissionRoute } from "utils"

// Types
interface Type extends LinkProps {
  permission?: string
  className?: string
  /** When true, absolute `http://` or `https://` links render as `<a target="_blank">` instead of in-app navigation. */
  openInNewTab?: boolean
}

function isAbsoluteHttpUrl(to: LinkProps["to"]): to is string {
  return typeof to === "string" && /^https?:\/\//i.test(to.trim())
}

export function PermissionLink(params: PropsWithChildren<Type>): JSX.Element {
  // Vars
  const { children, permission, openInNewTab, className, to, ...rest } = params

  // Hooks
  const permissionRoute = usePermissionRoute()

  const allowed =
    !permission ||
    Boolean(
      permission &&
        permissionRoute.checkPermission(permission, { disableAlert: true })
    )

  if (!allowed) {
    return (
      <div
        className={`cursor-pointer w-full ${className ?? ""}`}
        onClick={() => permissionRoute.checkPermission(permission)}
      >
        {children}
      </div>
    )
  }

  if (openInNewTab && isAbsoluteHttpUrl(to)) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {children}
      </a>
    )
  }

  return (
    <Link to={to} className={className} {...rest}>
      {children}
    </Link>
  )
}